import { formatoNumero } from '@/lib/utils/formato-precio';
import type { Modulo } from '@/components/layout/modulos';
import type { PricePositionProduct } from '@/features/dynamic-pricing/types/dynamic-pricing.types';

/** «En línea» es ±2 % del más barato: la misma banda que Análisis de mercado. */
export const BANDA_EN_LINEA = 2;

/** Cuántos productos estoy más caro, en línea y más barato que el más barato. */
export function posicionContraElMercado(productos: PricePositionProduct[]): {
  masCaros: number;
  enLinea: number;
  masBaratos: number;
  conCompetencia: number;
} {
  const con = productos.filter((p) => p.gapVsMin != null && p.competitorMinPrice != null);
  const masCaros = con.filter((p) => (p.gapVsMin as number) > BANDA_EN_LINEA).length;
  const masBaratos = con.filter((p) => (p.gapVsMin as number) < -BANDA_EN_LINEA).length;
  return {
    masCaros,
    enLinea: con.length - masCaros - masBaratos,
    masBaratos,
    conCompetencia: con.length,
  };
}

/** Variación porcentual contra el período anterior; null sin base para comparar. */
export function variacion(actual: number, anterior: number): number | null {
  if (!Number.isFinite(actual) || !Number.isFinite(anterior) || anterior <= 0) return null;
  return ((actual - anterior) / anterior) * 100;
}

/** «USD 2,73 M», «USD 12,4 k», «USD 980»: el monto grande de una tarjeta. */
export function montoCorto(monto: number, moneda = 'USD'): string {
  const n = Number.isFinite(monto) ? monto : 0;
  const abs = Math.abs(n);
  // Con el formateador único de la app (el guardián `moneda-fija` no deja otro).
  if (abs >= 1_000_000) return `${moneda} ${formatoNumero(n / 1_000_000, 2)} M`;
  if (abs >= 10_000) return `${moneda} ${formatoNumero(n / 1_000, 1)} k`;
  return `${moneda} ${formatoNumero(n, 0)}`;
}

/** Una cosa pendiente de «Necesita tu atención». */
export interface Pendiente {
  id: string;
  modulo: Modulo;
  titulo: string;
  detalle: string;
  accion: string;
  href: string;
}

/** Lo que cada módulo aporta a «Necesita tu atención» (sólo lo que tiene cuenta). */
export interface CuentasPendientes {
  aprobaciones?: number;
  cotizacionesPorVencer?: number;
  preciosPorDecidir?: number;
  avisosSinVer?: number;
  lecturasFallidas?: number;
  tarifasVencidas?: number;
  tarifasPorVencer?: number;
  importacionesPorVencer?: number;
}

const plural = (n: number, uno: string, varios: string) =>
  `${n.toLocaleString('es-BO')} ${n === 1 ? uno : varios}`;

/**
 * Arma la lista de pendientes, en el orden del día: primero lo que espera una
 * decisión de la persona, después lo que se vence, al final lo informativo.
 * Cero no es pendiente: no se lista.
 */
export function pendientesDelDia(c: CuentasPendientes): Pendiente[] {
  const lista: Pendiente[] = [];
  if (c.aprobaciones)
    lista.push({
      id: 'aprobaciones',
      modulo: 'cotiza',
      titulo: plural(c.aprobaciones, 'aprobación pendiente', 'aprobaciones pendientes'),
      detalle: 'Cotizaciones que esperan tu firma para seguir.',
      accion: 'Revisar',
      href: '/approvals',
    });
  if (c.preciosPorDecidir)
    lista.push({
      id: 'precios',
      modulo: 'compite',
      titulo: plural(
        c.preciosPorDecidir,
        'precio espera tu decisión',
        'precios esperan tu decisión'
      ),
      detalle: 'Propuestas de tus estrategias, listas para aprobar o descartar.',
      accion: 'Revisar precios',
      href: '/compite/decisiones/revisar',
    });
  if (c.cotizacionesPorVencer)
    lista.push({
      id: 'por-vencer',
      modulo: 'cotiza',
      titulo: plural(
        c.cotizacionesPorVencer,
        'cotización vence pronto',
        'cotizaciones vencen pronto'
      ),
      detalle: 'Dales seguimiento antes de que pierdan vigencia.',
      accion: 'Ver',
      href: '/quotes',
    });
  if (c.avisosSinVer)
    lista.push({
      id: 'avisos',
      modulo: 'compite',
      titulo: plural(
        c.avisosSinVer,
        'cambio de la competencia sin ver',
        'cambios de la competencia sin ver'
      ),
      detalle: 'Precios que subieron o bajaron en las tiendas que seguís.',
      accion: 'Ver avisos',
      href: '/compite/monitoreo?tab=avisos',
    });
  if (c.tarifasVencidas)
    lista.push({
      id: 'tarifas-vencidas',
      modulo: 'comercia',
      titulo: plural(c.tarifasVencidas, 'tarifa de flete vencida', 'tarifas de flete vencidas'),
      detalle: 'Las cotizaciones de importación no las usan hasta que se actualicen.',
      accion: 'Actualizar',
      href: '/trade/tarifario',
    });
  if (c.importacionesPorVencer)
    lista.push({
      id: 'importaciones',
      modulo: 'comercia',
      titulo: plural(
        c.importacionesPorVencer,
        'cotización de importación vence esta semana',
        'cotizaciones de importación vencen esta semana'
      ),
      detalle: 'El cliente todavía no respondió y el precio está por vencer.',
      accion: 'Ver',
      href: '/trade/cotizaciones',
    });
  if (c.tarifasPorVencer)
    lista.push({
      id: 'tarifas-por-vencer',
      modulo: 'comercia',
      titulo: plural(c.tarifasPorVencer, 'tarifa vence pronto', 'tarifas vencen pronto'),
      detalle: 'Pedí la renovación a tu proveedor de flete.',
      accion: 'Ver tarifario',
      href: '/trade/tarifario',
    });
  if (c.lecturasFallidas)
    lista.push({
      id: 'lecturas',
      modulo: 'compite',
      titulo: plural(
        c.lecturasFallidas,
        'lectura de precios falló',
        'lecturas de precios fallaron'
      ),
      detalle: 'En las últimas 24 horas no se pudo leer alguna tienda.',
      accion: 'Ver lecturas',
      href: '/compite/competencia?tab=lecturas',
    });
  return lista;
}
